import type { DriverTarget } from "@skaile/workspaces/plugin-registry";
import type { Logger } from "@skaile/workspaces/types";
import * as z from "zod";
import { dispatchCapability } from "../capability-dispatch.js";
import { getBridgeLogger } from "../logger.js";
import { BUILTIN_DRIVER_CATALOG } from "../registry.js";
import {
  type AgentConfig,
  AgentDriver,
  type AgentEvent,
  type BridgeCapabilityTool,
  type DriverInfo,
} from "../types.js";
import {
  actionArgs,
  type FlowScript,
  decideFlowAction,
  isTurnEnding,
  parseFlowScript,
} from "./flow-script.js";

/**
 * Echo/mock driver for E2E testing in docker mode.
 *
 * Runs inside a real Docker container with real WebSocket transport and
 * session lifecycle, but generates deterministic keyword-triggered responses
 * instead of calling the Anthropic API. This lets E2E tests exercise the
 * full infrastructure (containers, WebSocket, session management, message
 * persistence) without needing real AI credentials.
 *
 * Keyword triggers match the MockAgentClient in
 * `platform/backend/libs/agent-gateway/src/mock-agent-client.ts` — the
 * same test messages ("test toolcall", "test question", etc.) produce
 * the same events.
 *
 * Enable by setting `SKAILE_AGENT_DRIVER=echo` in the container env,
 * or by passing `{ driver: 'echo' }` in the runner config.
 *
 * @docLink packages/bridge/drivers#echo-driver
 */
export class EchoDriver extends AgentDriver {
  readonly driverInfo: DriverInfo = {
    id: "echo",
    name: "Echo (E2E mock)",
    modelAgnostic: true,
    supportsInBandAbort: false,
  };

  private readonly log: Logger = getBridgeLogger("echo");
  private running = false;
  private readonly config: AgentConfig;

  /**
   * Scripted-flow mode state (workspaces#396). Enabled by a `[test-flow]`
   * sentinel chat message; once on, orchestrator-prompt turns are driven
   * deterministically against `config.flowTestDriver`. `null` script ⇒ mode
   * off (default echo behaviour, unchanged).
   */
  private flowScript: FlowScript | null = null;

  constructor(config: AgentConfig) {
    super();
    this.config = config;
  }

  /**
   * Test surface: returns the tool list the LLM would see for this turn.
   * Composition: the registry hooks (Protocol v2) take precedence; legacy
   * `mcpServers` is not enumerated here (the echo driver never exposed MCP
   * tools to the LLM by name).
   */
  listCapabilityTools(): BridgeCapabilityTool[] {
    return this.config.capabilities?.composeTools() ?? [];
  }

  /**
   * Test surface: invoke a registered capability through the dispatch
   * helper. Mirrors what a real driver would do when the LLM produced a
   * tool_use for the given name — applies fire-and-forget, render emission,
   * and fallback text. Returns the structured dispatch result so tests can
   * assert immediate-vs-background resolution.
   */
  async invokeCapabilityForTest(
    name: string,
    input: unknown,
  ): Promise<{ result: unknown; background: boolean }> {
    const hooks = this.config.capabilities;
    if (!hooks) throw new Error("EchoDriver: no capability hooks configured");
    const callId = `echo-cap-${Date.now()}`;
    const out = await dispatchCapability(hooks, callId, name, input, (err) =>
      this.log.warn("background capability handler failed", {
        name,
        error: String(err),
      }),
    );
    return out;
  }

  async start(): Promise<void> {
    this.running = true;
    this.log.info("driver started");
  }

  async prompt(message: string): Promise<void> {
    if (!this.running) return;
    const content = message.toLowerCase();
    this.log.info("received prompt", { preview: message.slice(0, 80), length: message.length });

    // Small delay to simulate real agent processing
    await new Promise((resolve) => setTimeout(resolve, 200));

    // Scripted-flow test seam (workspaces#396) — a `[test-flow]` sentinel
    // arms deterministic flow driving for this session and (optionally)
    // carries a JSON script. Format: `[test-flow] {json}`; empty body ⇒
    // canonical policy. Must be handled before the flow-turn branch below.
    //
    // Detection is LINE-anchored, not message-anchored (workspaces#401): hosts
    // decorate user messages (e.g. a prepended `<SESSION_MEMBERS>` block and a
    // wake-restoration preamble), so the sentinel is rarely at the start of the
    // composed prompt. We match `[test-flow]` at the start of ANY line (only
    // leading whitespace allowed), which is decoration-independent yet still
    // impossible to arm from ordinary prose — a line must BEGIN with the exact
    // token. The optional JSON script must sit on that same line.
    const flowMatch = /^[ \t]*\[test-flow\][ \t]*(.*)$/m.exec(message);
    if (flowMatch) {
      let summary: string;
      try {
        this.flowScript = parseFlowScript(flowMatch[1] ?? "");
        summary = "echo[test-flow]: scripted-flow mode armed";
      } catch (err) {
        this.flowScript = null;
        summary = `echo[test-flow]: invalid script — ${err instanceof Error ? err.message : String(err)}`;
        this.emit("agent-event", { type: "error", error: summary, fatal: false });
      }
      for (const event of textAndResult(summary)) this.emit("agent-event", event);
      this.emit("agent-event", { type: "agent_end" });
      return;
    }

    // Scripted-flow turn — the runner reassembles the orchestrator prompt on
    // every flow turn; when scripted-flow mode is armed and a flow driver hook
    // is present, drive nodes deterministically instead of echoing text.
    if (this.flowScript && this.config.flowTestDriver && isFlowTurn(message)) {
      await this.runScriptedFlowTurn();
      return;
    }

    // Phase 7B test seam — explicit capability invocation keyword. Lets E2E
    // tests drive `dispatchCapability` from a chat prompt without a real LLM.
    // Format: `[test-cap:<name>] {json}`, LINE-anchored like `[test-flow]` above
    // because hosts decorate user messages. Both flags are load-bearing and are
    // pinned by tests: no `s` (dotAll swallows the host's closing tag into the
    // JSON body), and `[ \t]` not `\s` (`\s` un-anchors the token from its line).
    const capMatch = /^[ \t]*\[test-cap:([\w.\-_]+)\][ \t]*(.*)$/m.exec(message);
    if (capMatch) {
      const name = capMatch[1] ?? "";
      const rawInput = (capMatch[2] ?? "").trim();
      let input: unknown = {};
      if (rawInput.length > 0) {
        try {
          input = JSON.parse(rawInput);
        } catch (err) {
          this.log.warn("test-cap input was not valid JSON; defaulting to {}", {
            error: String(err),
            preview: rawInput.slice(0, 80),
          });
        }
      }
      const events = await this.runCapabilityKeyword(name, input);
      for (const event of events) {
        this.emit("agent-event", event);
        await new Promise((resolve) => setTimeout(resolve, 20));
      }
      this.emit("agent-event", { type: "agent_end" });
      return;
    }

    const events = buildEchoEvents(content, message);
    for (const event of events) {
      this.emit("agent-event", event);
      // Small gap between events to let the transport process each one
      await new Promise((resolve) => setTimeout(resolve, 50));
    }
    // Signal turn completion — the runner waits for this before accepting
    // the next prompt. Without it, the runner stays stuck after the first message.
    this.emit("agent-event", { type: "agent_end" });
  }

  /**
   * Phase 7B helper — drive a single capability through the dispatcher and
   * surface the structured outcome as a result event the LLM-side test code
   * can assert against. Used exclusively by the `[test-cap:<name>]` prompt
   * keyword above; never wired into normal chat flow.
   */
  private async runCapabilityKeyword(name: string, input: unknown): Promise<AgentEvent[]> {
    const events: AgentEvent[] = [];
    if (!this.config.capabilities) {
      return textAndResult(`echo[test-cap]: no capability hooks configured (skipping ${name})`);
    }
    try {
      const out = await this.invokeCapabilityForTest(name, input);
      const summary = `echo[test-cap]: invoked ${name} input=${safeStringify(input)} background=${String(out.background)} result=${safeStringify(out.result)}`;
      events.push(...textAndResult(summary));
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      events.push({
        type: "error",
        error: `echo[test-cap]: ${name} failed: ${msg}`,
        fatal: false,
      });
      events.push(...textAndResult(`echo[test-cap]: ${name} errored (${msg})`));
    }
    return events;
  }

  /**
   * Scripted-flow turn (workspaces#396). Reads the live flow state via the
   * runner-injected `flowTestDriver` hook and executes the scripted next
   * action(s) until the policy yields or emits a turn-ending action. Each
   * mutation fires the adapter's `onChange` (→ host `state_changed`) and, when
   * the runner wires the stimulus bus, queues the follow-up turn — so the
   * driver advances one step at a time exactly like a real LLM orchestrator.
   */
  private async runScriptedFlowTurn(): Promise<void> {
    const hook = this.config.flowTestDriver;
    const script = this.flowScript;
    if (!hook || !script) {
      this.emit("agent-event", { type: "agent_end" });
      return;
    }

    const executed: string[] = [];
    // Hard cap — the policy is monotonic, but never let a bug spin forever.
    const MAX_STEPS = 200;
    for (let i = 0; i < MAX_STEPS; i++) {
      const state = hook.getState();
      if (!state) break;
      const mandatoryGates = new Set(hook.mandatoryGates?.() ?? []);
      const action = decideFlowAction(state, script, mandatoryGates);
      if (!action) break;

      this.emit("agent-event", {
        type: "tool_call",
        name: action.op,
        tool: { name: action.op },
        input: action.nodeId,
      });
      try {
        await hook.execute(action.op, actionArgs(action));
      } catch (err) {
        const msg = err instanceof Error ? err.message : String(err);
        this.log.warn("scripted flow op failed", {
          op: action.op,
          nodeId: action.nodeId,
          error: msg,
        });
        this.emit("agent-event", {
          type: "error",
          error: `echo[test-flow]: ${action.op}(${action.nodeId}) failed: ${msg}`,
          fatal: false,
        });
        break;
      }
      this.emit("agent-event", { type: "tool_execution_end", toolName: action.op });
      executed.push(`${action.op}(${action.nodeId})`);
      if (isTurnEnding(action)) break;
    }

    const summary =
      executed.length > 0
        ? `echo[test-flow]: ${executed.join(" → ")}`
        : "echo[test-flow]: no action (yielding to host)";
    for (const event of textAndResult(summary)) this.emit("agent-event", event);
    this.emit("agent-event", { type: "agent_end" });
  }

  async abort(): Promise<void> {
    this.log.info("abort requested");
  }

  kill(): void {
    this.running = false;
    this.log.info("killed");
  }

  async resetSession(): Promise<void> {
    this.log.info("session reset");
  }

  get isRunning(): boolean {
    return this.running;
  }
}

// ---------------------------------------------------------------------------
// Echo response generation — mirrors MockAgentClient.buildMockEvents
// ---------------------------------------------------------------------------

/**
 * A flow turn is recognised by the orchestrator prompt's stable header
 * (`buildOrchestratorPrompt` in the flow connector). Detection is header-only —
 * the driver reads live state through the injected hook, never by parsing the
 * prompt body.
 */
function isFlowTurn(message: string): boolean {
  return message.startsWith("# Flow Execution Context");
}

function safeStringify(v: unknown): string {
  try {
    return JSON.stringify(v);
  } catch {
    return String(v);
  }
}

function textAndResult(summary: string): AgentEvent[] {
  return [
    {
      type: "message_start",
      message: { role: "assistant", content: summary },
    },
    {
      type: "message_end",
      message: { role: "assistant", content: summary },
    },
    {
      type: "result",
      subtype: "success",
      summary,
      costUsd: 0,
    },
  ];
}

function buildEchoEvents(content: string, originalPrompt: string): AgentEvent[] {
  const events: AgentEvent[] = [];

  if (content.includes("test all")) {
    events.push(
      { type: "tool_call", name: "Read", tool: { name: "Read" }, input: "prisma/schema.prisma" },
      { type: "tool_execution_end", toolName: "Read" },
      {
        type: "tool_call",
        name: "Write",
        tool: { name: "Write" },
        input: "src/modules/sprint/sprint.service.ts",
      },
      { type: "tool_execution_end", toolName: "Write" },
    );
    events.push(
      ...textAndResult(
        "All operations completed successfully. Created sprint service, updated schema, started dev server, and ran E2E tests.",
      ),
    );
  } else if (content.includes("test toolcall")) {
    events.push(
      {
        type: "tool_call",
        name: "Read",
        tool: { name: "Read" },
        input: "platform/prisma/schema.prisma",
      },
      { type: "tool_execution_end", toolName: "Read" },
    );
    events.push(
      ...textAndResult(
        "I've read the schema and found 42 models. The data model looks well-structured.",
      ),
    );
  } else if (content.includes("test question")) {
    // Emit a message that asks the user a question (the runner normalizer
    // converts this to a `question` AgentEvent for the transport layer)
    events.push(
      ...textAndResult(
        "The blueprint calls for a relational database. Which engine should I target? " +
          "This will affect the Prisma provider, Docker Compose setup, and the connection string.\n\n" +
          "Options: PostgreSQL, MySQL, SQLite",
      ),
    );
  } else if (content.includes("test error")) {
    events.push({
      type: "error",
      error:
        "AI provider returned HTTP 429 — rate limit exceeded. The request will be retried automatically in 35 seconds.",
      fatal: false,
    });
  } else if (content.includes("test fatal")) {
    events.push({
      type: "error",
      error:
        "Session agent was killed by the container runtime (OOM — memory limit 2 GB exceeded). The session cannot continue.",
      fatal: true,
    });
  } else {
    // Default echo response
    const summary =
      `I received your message: "${originalPrompt}". I'm the Skaile AI assistant running in echo mode. ` +
      `This is a deterministic mock response for E2E testing.`;
    events.push(...textAndResult(summary));
  }

  return events;
}

/**
 * `DriverTarget` wrapper for the echo driver. Registered into the plugin
 * registry by `registerBuiltinDrivers()`.
 */
export const echoDriverTarget: DriverTarget<AgentConfig> = {
  id: "echo",
  displayName: BUILTIN_DRIVER_CATALOG.echo?.name ?? "Echo (E2E mock)",
  apiVersion: 1,
  // Drivers take AgentConfig; passthrough — full schema validation is out of scope.
  configSchema: z.custom<AgentConfig>(() => true),
  create: async (config, _ctx) => new EchoDriver(config),
  listModels: async (apiKeys) =>
    new EchoDriver({ cwd: process.cwd(), sessionId: "model-list", apiKeys }).listModels(),
};
